import {assert} from 'Asserts';
import {SystemColumnName} from 'data-frames/DataFrameConstants';
import {DataFrameSpec} from 'data-frames/DataFrameTypes';
import findAndAssertColumn from 'data-frames/util/findAndAssertColumn';
import findColumn from 'data-frames/util/findColumn';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {AnalysisFormulaParsingError} from 'pipelines/formula/Constants';
import formulaTextToFormulaAstJson from 'pipelines/formula/formulaTextToFormulaAstJson';
import {PipelineStepConfig} from 'pipelines/PipelineTypes';
import findDuplicates from 'util/findDuplicates';
import removeNulls from 'util/removeNulls';

function getDuplicateColumnsDisplayText(
  dataFrameSpec: DataFrameSpec,
  systemNames: Array<string>
): string | null {
  // Find all system names that are duplicated and convert them to display names
  const duplicateDisplayNames = findDuplicates(systemNames).map(
    (systemName) => findAndAssertColumn(dataFrameSpec, systemName).displayName
  );
  return duplicateDisplayNames.length === 0 ? null : duplicateDisplayNames.join(', ');
}

function getDuplicateColumnsMessage(
  dataFrameSpec: DataFrameSpec,
  systemNames: Array<string>,
  messagePrefix: string,
  columnNamesPrefix: string = 'Duplicate columns: '
): string | null {
  const duplicatedColumnsDisplayText = getDuplicateColumnsDisplayText(dataFrameSpec, systemNames);
  return duplicatedColumnsDisplayText == null
    ? null
    : `${messagePrefix} ${columnNamesPrefix}${duplicatedColumnsDisplayText}`;
}

function getMissingColumnsMessage(dataFrameSpec: DataFrameSpec, systemNames: Array<string>): string | null {
  const selectedColumnSet = new Set(systemNames);
  const missingColumnDisplayNames = [];
  for (const col of dataFrameSpec.columns) {
    if (col.systemName === SystemColumnName.IS_EXCLUDED || col.systemName === SystemColumnName.ROW_ID) {
      // ignore system cols
      continue;
    }
    if (!selectedColumnSet.has(col.systemName)) {
      missingColumnDisplayNames.push(col.displayName);
    }
  }
  if (missingColumnDisplayNames.length === 0) {
    return null;
  }

  return (
    'Must specify every column while reordering. ' +
    `Missing columns: ${missingColumnDisplayNames.join(', ')}`
  );
}

function getColumnsDisplayText(dataFrameSpec: DataFrameSpec, systemNames: Array<string>): string | null {
  const names = systemNames.map((systemName) => findAndAssertColumn(dataFrameSpec, systemName).displayName);
  return names.length === 0 ? null : names.join(', ');
}

function getOverlappedColumnsMessage(
  dataFrameSpec: DataFrameSpec,
  systemNames1: Array<string>,
  systemNames2: Array<string>,
  messagePrefix: string
): string | null {
  const overlappedColumns = systemNames1.filter((systemName) => systemNames2.includes(systemName));
  return overlappedColumns.length === 0
    ? null
    : `${messagePrefix} Duplicate columns: ${getColumnsDisplayText(dataFrameSpec, overlappedColumns)}`;
}

export type ConfigValidationFunction<
  StepType extends PipelineStepType,
  StepVersion extends number,
  Status extends 'draft' | 'complete' = 'complete',
> = (
  config: PipelineStepConfig<StepType, StepVersion, Status>,
  dataFrameSpec: DataFrameSpec
) => Array<string>;

export const validateAggregateConfig: ConfigValidationFunction<PipelineStepType.AGGREGATE_DATASET, 0> = (
  config,
  dataFrameSpec
) => {
  const duplicateGroupColumnsMsg = getDuplicateColumnsMessage(
    dataFrameSpec,
    config.groupColumns,
    'Cannot group by the same column more than once.'
  );
  // Get column names that are both being aggregated and grouped
  const columnsBeingAggregatedAndGroupedMsg = getDuplicateColumnsMessage(
    dataFrameSpec,
    [
      ...new Set(config.groupColumns),
      ...new Set(
        config.aggregations
          .filter(
            (agg): agg is Exclude<typeof agg, {aggregationFunction: 'COUNT'}> =>
              agg.aggregationFunction !== 'COUNT'
          )
          .map((agg) => agg.column)
      ),
    ],
    'Cannot group by a column and aggregate it.',
    'Columns being aggregated and grouped: '
  );

  const duplicatedOrderByColumnsMsgs = config.aggregations
    .filter(
      (agg): agg is Extract<typeof agg, {aggregationFunction: 'STRING_AGG'}> =>
        agg.aggregationFunction === 'STRING_AGG' && agg.orderByColumns != null
    )
    .map((agg) =>
      getDuplicateColumnsMessage(
        dataFrameSpec,
        agg.orderByColumns.map((orderByColumn) => orderByColumn.column),
        'Cannot order by the same column more than once.'
      )
    );

  return removeNulls([
    duplicateGroupColumnsMsg,
    columnsBeingAggregatedAndGroupedMsg,
    ...duplicatedOrderByColumnsMsgs,
  ]);
};

export const validateBinDataConfig: ConfigValidationFunction<PipelineStepType.BIN_DATA, 0> = (
  config,
  dataFrameSpec
) => [];

export const validateComputedColumnConfig: ConfigValidationFunction<
  PipelineStepType.COMPUTED_COLUMN,
  0,
  'draft'
> = (config, dataFrameSpec) => {
  if (!config.formula.formulaText || config.formula.formulaAst != null) {
    return [];
  }
  try {
    formulaTextToFormulaAstJson(config.formula.formulaText);
    assert(false, 'Config validation has valid formula text but null formula AST');
  } catch (e) {
    if (e instanceof AnalysisFormulaParsingError) {
      return [e.message];
    }
    throw e;
  }
};

export const validatePivotConfig: ConfigValidationFunction<PipelineStepType.PIVOT_DATASET, 0> = (
  config,
  dataFrameSpec
) => {
  const duplicatePivotColumnsMsg = getDuplicateColumnsMessage(
    dataFrameSpec,
    config.pivotColumns,
    'Cannot pivot by the same column more than once.'
  );
  const duplicateGroupColumnsMsg = getDuplicateColumnsMessage(
    dataFrameSpec,
    config.groupColumns,
    'Cannot group by the same column more than once.'
  );

  // pivot and group by columns should not be used as value columns
  const valueColumnsUsedAsPivotColumns = config.valueColumns
    .map((valueColumn) => valueColumn.column)
    .filter((columnName) => config.pivotColumns.includes(columnName))
    .map((systemName) => findAndAssertColumn(dataFrameSpec, systemName).displayName);
  const valueColumnsUsedAsPivotColumnsMsg =
    valueColumnsUsedAsPivotColumns.length === 0
      ? null
      : `Cannot use a column both as a pivot column and a value column. Columns being used as both pivot and value: ${valueColumnsUsedAsPivotColumns}`;

  const valueColumnsUsedAsGroupByColumns = config.valueColumns
    .map((valueColumn) => valueColumn.column)
    .filter((columnName) => config.groupColumns.includes(columnName))
    .map((systemName) => findAndAssertColumn(dataFrameSpec, systemName).displayName);
  const valueColumnsUsedAsGroupByColumnsMsg =
    valueColumnsUsedAsGroupByColumns.length === 0
      ? null
      : `Cannot use a column both as a group by column and a value column. Columns being used as both group by and value: ${valueColumnsUsedAsGroupByColumns}`;

  return removeNulls([
    duplicatePivotColumnsMsg,
    duplicateGroupColumnsMsg,
    valueColumnsUsedAsPivotColumnsMsg,
    valueColumnsUsedAsGroupByColumnsMsg,
  ]);
};

export const validateSortConfig: ConfigValidationFunction<PipelineStepType.SORT_COLUMNS, 0> = (
  config,
  dataFrameSpec
) => {
  const duplicateMsg = getDuplicateColumnsMessage(
    dataFrameSpec,
    config.sortOptions.map(({column}) => column),
    'Cannot sort the same column more than once.'
  );
  return duplicateMsg == null ? [] : [duplicateMsg];
};

export const validateUnpivotConfig: ConfigValidationFunction<PipelineStepType.UNPIVOT_DATASET, 0> = (
  config,
  dataFrameSpec
) => {
  const duplicateUnpivotColumnsMsg = getDuplicateColumnsMessage(
    dataFrameSpec,
    config.unpivotColumns,
    'Cannot unpivot by the same column more than once.'
  );

  const existingNameColumn = findColumn(dataFrameSpec, config.nameColumn);
  const existingNameColumnMsg =
    existingNameColumn == null
      ? null
      : `Cannot use existing column name for name column: ${config.nameColumn}`;

  const existingValueColumn = findColumn(dataFrameSpec, config.valueColumn);
  const existingValueColumnMsg =
    existingValueColumn == null
      ? null
      : `Cannot use existing column name for value column: ${config.valueColumn}`;

  const conflictingNameAndValueColumnMsg =
    config.nameColumn === config.valueColumn ? 'Name and value columns cannot be the same' : null;

  return removeNulls([
    duplicateUnpivotColumnsMsg,
    existingNameColumnMsg,
    existingValueColumnMsg,
    conflictingNameAndValueColumnMsg,
  ]);
};

export const validateWindowFunctionsConfig: ConfigValidationFunction<PipelineStepType.WINDOW_FUNCTIONS, 0> = (
  config,
  dataFrameSpec
) => {
  const duplicateOrderByColumnsMsg = getDuplicateColumnsMessage(
    dataFrameSpec,
    config.orderByColumns.map((orderByColumn) => orderByColumn.column),
    'Cannot order by the same column more than once.'
  );
  const duplicateOrderByColumnsInWindowMsgs = config.windows.map((window) =>
    getDuplicateColumnsMessage(
      dataFrameSpec,
      window.orderByColumns.map((orderByColumn) => orderByColumn.column),
      'Cannot order by the same column more than once.'
    )
  );

  const duplicatePartitionColumnsInWindowMsgs = config.windows.map((window) =>
    getDuplicateColumnsMessage(
      dataFrameSpec,
      window.partitionColumns,
      'Cannot partition by the same column more than once.'
    )
  );

  return removeNulls([
    duplicateOrderByColumnsMsg,
    ...duplicateOrderByColumnsInWindowMsgs,
    ...duplicatePartitionColumnsInWindowMsgs,
  ]);
};

export const validateFindAndReplaceConfig: ConfigValidationFunction<PipelineStepType.FIND_AND_REPLACE, 0> = (
  config,
  dataFrameSpec
) => {
  const duplicateSelectedColumnsMsg = getDuplicateColumnsMessage(
    dataFrameSpec,
    config.selectedColumns,
    'Cannot find and replace by the same column more than once.'
  );
  const emptyliteralStringMsg =
    config.findAndReplaceOptions.findAndReplaceType !== 'LITERAL' ||
    config.findAndReplaceOptions.literalString?.length > 0
      ? null
      : 'Find text must be at least one character';
  const emptyPatternStringMsg =
    config.findAndReplaceOptions.findAndReplaceType !== 'REGEX' ||
    config.findAndReplaceOptions.patternString?.length > 0
      ? null
      : 'Regex pattern must be at least one character';
  return removeNulls([duplicateSelectedColumnsMsg, emptyliteralStringMsg, emptyPatternStringMsg]);
};

export const validateHideColumnsConfig: ConfigValidationFunction<PipelineStepType.HIDE_COLUMNS, 0> = (
  config,
  dataFrameSpec
) => {
  const duplicateColumnsMsg = getDuplicateColumnsMessage(
    dataFrameSpec,
    config.columns,
    'Cannot hide the same column more than once.'
  );
  const duplicatedColumnsToKeepMsg = getDuplicateColumnsMessage(
    dataFrameSpec,
    config.columnsToKeep,
    'Cannot keep the same column more than once.'
  );

  const hideAndKeepColumnsOverlapMsg =
    config.columns.length > 0 && config.columnsToKeep.length > 0
      ? 'Columns to hide and columns to keep cannot be set at the same time.'
      : null;

  const noColumnsMsg =
    config.columns.length >= 1 || config.columnsToKeep.length >= 1
      ? null
      : 'Must hide or keep at least one column.';

  return removeNulls([
    duplicateColumnsMsg,
    duplicatedColumnsToKeepMsg,
    noColumnsMsg,
    hideAndKeepColumnsOverlapMsg,
  ]);
};

export const validateRetypeColumnsConfig: ConfigValidationFunction<PipelineStepType.RETYPE_COLUMNS, 0> = (
  config,
  dataFrameSpec
) => {
  const columns = config.retypeColumns.map((rc) => rc.column);
  const duplicateColumnsMsg = getDuplicateColumnsMessage(
    dataFrameSpec,
    columns,
    'Cannot convert the same column more than once.'
  );
  const noColumnsMsg = columns.length >= 1 ? null : 'Must convert at least one column';
  return removeNulls([duplicateColumnsMsg, noColumnsMsg]);
};

export const validateRenameColumnsConfig: ConfigValidationFunction<PipelineStepType.RENAME_COLUMNS, 0> = (
  config,
  dataFrameSpec
) => {
  const columns = config.renameColumns.map((rc) => rc.column);
  const duplicateColumnsMsg = getDuplicateColumnsMessage(
    dataFrameSpec,
    columns,
    'Cannot rename the same column more than once.'
  );

  const noColumnsMsg = columns.length >= 1 ? null : 'Must rename at least one column';
  const emptyNames = config.renameColumns.filter((rc) => rc.name.length === 0).map((rc) => rc.column);
  const emptyNameMsg =
    emptyNames.length === 0
      ? null
      : `New name must be at least one character. Columns to fix: ${getColumnsDisplayText(
          dataFrameSpec,
          emptyNames
        )}`;

  return removeNulls([duplicateColumnsMsg, noColumnsMsg, emptyNameMsg]);
};

export const validateReorderColumnsConfig: ConfigValidationFunction<PipelineStepType.REORDER_COLUMNS, 0> = (
  config,
  dataFrameSpec
) => {
  const duplicateColumnsMsg = getDuplicateColumnsMessage(
    dataFrameSpec,
    config.reorderColumns,
    'Cannot specify the same column more than once while reordering.'
  );
  const missingColumnsMsg = getMissingColumnsMessage(dataFrameSpec, config.reorderColumns);

  return removeNulls([duplicateColumnsMsg, missingColumnsMsg]);
};

export const validateParameterOptimizationConfigurationConfig: ConfigValidationFunction<
  PipelineStepType.PARAMETER_OPTIMIZATION_CONFIGURATION,
  0
> = (config, dataFrameSpec) => {
  const columnsToValidateDuplication = [
    {columns: config.featureColumns, messagePrefix: 'Cannot use a column as a feature more than once.'},
    {columns: config.excludedColumns, messagePrefix: 'Cannot exclude a column more than once.'},
    {
      columns: config.metadataColumns,
      messagePrefix: 'Cannot use a column as metadata more than once.',
    },
  ];

  const duplicateColumnsMessages = columnsToValidateDuplication.map((data) =>
    getDuplicateColumnsMessage(dataFrameSpec, data.columns, data.messagePrefix)
  );

  const featuresAndExcludeBothSetMessage =
    config.featureColumns.length > 0 && config.excludedColumns.length > 0
      ? 'Feature columns and Columns to exclude cannot be set at the same time.'
      : null;

  const columnsToValidateOverlap = [
    {
      columns1: config.featureColumns,
      columns2: config.metadataColumns,
      messagePrefix: 'Cannot both use a column as a feature and as metadata.',
    },
    {
      columns1: config.excludedColumns,
      columns2: config.metadataColumns,
      messagePrefix: 'Cannot both use a column as metadata and exclude it.',
    },
    {
      columns1: config.excludedColumns,
      columns2: config.groupingColumn ? [config.labelColumn, config.groupingColumn] : [config.labelColumn],
      messagePrefix: 'Cannot exclude a column if it is used as the Y variable or Grouping column.',
    },
    {
      columns1: config.featureColumns,
      columns2: config.groupingColumn ? [config.labelColumn, config.groupingColumn] : [config.labelColumn],
      messagePrefix:
        'Cannot set a column as feature column if it is used as the Y variable or Grouping column.',
    },
  ];

  const overlappedColumnsMessages = columnsToValidateOverlap.map((data) =>
    getOverlappedColumnsMessage(dataFrameSpec, data.columns1, data.columns2, data.messagePrefix)
  );

  return removeNulls([
    ...duplicateColumnsMessages,
    featuresAndExcludeBothSetMessage,
    ...overlappedColumnsMessages,
  ]);
};

// TODO(BNCH-99524): Access step input parents in order to validate the config
// against the regression series.
export const validateRegressionInterpolationConfig: ConfigValidationFunction<
  PipelineStepType.REGRESSION_INTERPOLATION,
  0
> = (config, dataFrameSpec) => [];

// TODO(BNCH-105159): Access step input parents in order to validate the config
// against the regression series.
export const validateChartViewConfig: ConfigValidationFunction<PipelineStepType.CHART_VIEW, 0> = (
  config,
  dataFrameSpec
) => [];
